import * as React from 'react';
import {useDispatch} from 'react-redux';
// @ts-expect-error
import type {RouterHistory} from 'react-router-dom';

import {api} from 'nutshell-core/api';
import {Nut_Settings_User} from 'nutshell-core/settings-constants';
import type {User} from 'nutshell-core/users/users-types';
import {getIdFromApiId} from 'nutshell-core/utils/get-id-from-api-id';

import {AiFeatureType} from 'nutshell-graphql-types';

import {useGetSetting} from '../core/use-get-setting';
import {useCoreEntityScrollCheckpoint} from '../core-entity/hooks/use-core-entity-scroll-checkpoint';
import {CoreEntityContentContainer} from '../core-entity/layout/core-entity-content-container';
import {CoreEntityMainColumn} from '../core-entity/layout/core-entity-main-column';
import {CoreEntityPageContainer} from '../core-entity/layout/core-entity-page-container';
import {LoadingPage} from '../core-entity/layout/loading-page';
import {TAB_ACTIVITY, TAB_NOTE} from '../core-entity/redux/consts';
import {setActionBarTab} from '../core-entity/redux/core-entity-page-reducer';
import {useIsAiFeatureEnabled} from '../setup/ai-features/graphql/hooks/use-is-ai-feature-enabled';
import {setAgentContext} from '../team-ai/redux/agent-reducer';
import {LEAD_PAGE} from '../team-ai/redux/consts';

import {ContentM3} from './content/content-m3';
import {Header} from './header/header';
import {LeadLimitUpsellModal} from './shared/components';
import {useGetLead, useGetLeadTags} from './shared/graphql/hooks';
import {useUpdateLeadHandlers} from './shared/hooks';
import {Sidebar} from './sidebar/sidebar';

type Props = {
    leadId?: string;
    leadNum?: number;
    history: RouterHistory;
    user: User;
};

export function LeadPage(props: Props) {
    const {
        hasScrolledPastCheckpoint,
        scrollContainerTop,
        scrollContainerEl,
        setScrollContainerRef,
        setActionBarRef,
    } = useCoreEntityScrollCheckpoint();
    const hasViewedLead = React.useRef<boolean>(false);

    const {
        lead,
        isLoading: isLoadingLead,
        error: errorMessage,
        refetch: refetchLead,
    } = useGetLead(props.leadId, props.leadNum);
    const {isEnabled: isLeadRecapFeatureEnabled} = useIsAiFeatureEnabled(
        AiFeatureType.LEAD_SUMMARIZER
    );
    const {fetchMore: fetchMoreTags} = useGetLeadTags();
    const {setting: isSidebarCollapsed, isFirstLoad: isSidebarCollapsedLoading} = useGetSetting({
        settingName: Nut_Settings_User.SETTING_LEAD_WORKSPACE_SIDEBAR_COLLAPSED,
    });
    const {setting: leadPageActionBarTab, isFirstLoad: isLeadPageActionBarTabLoading} =
        useGetSetting({
            settingName: Nut_Settings_User.SETTING_LEAD_PAGE_ACTION_BAR_TAB,
        });

    const dispatch = useDispatch();

    const {
        handleUpdateName,
        handleUpdateConfidence,
        handleUpdateDueDate,
        handleUpdateCreatedDate,
        handleUpdateClosedDate,
        handleUpdateTags,
    } = useUpdateLeadHandlers(lead);

    const sidebarCollapsedState = Boolean(
        isSidebarCollapsed && Number(isSidebarCollapsed.value) === 1
    );

    // When the tab loads via setting, update the redux state
    React.useEffect(() => {
        if (
            leadPageActionBarTab &&
            (leadPageActionBarTab.value === TAB_NOTE || leadPageActionBarTab.value === TAB_ACTIVITY)
        ) {
            dispatch(setActionBarTab(leadPageActionBarTab.value));
        }
    }, [leadPageActionBarTab, dispatch]);

    // Set the AI agent context for the lead page
    React.useEffect(() => {
        if (lead) {
            dispatch(setAgentContext(lead.id, LEAD_PAGE, lead.name));

            return () => {
                dispatch(setAgentContext(null, null, null));
            };
        }
    }, [dispatch, lead]);

    React.useEffect(() => {
        if (lead && !hasViewedLead.current) {
            hasViewedLead.current = true;
            api.post(`views/${lead.id}`, {});
        }
    }, [lead]);

    if (
        errorMessage === 'Lead limit exceeded' // Beware of changing- we string match an error from the back-end here
    ) {
        return <LeadLimitUpsellModal history={props.history} />;
    }

    if (!lead || isSidebarCollapsedLoading || isLoadingLead || isLeadPageActionBarTabLoading) {
        return <LoadingPage />;
    }

    const hasFullHeightSidebar = NutClientConfig.hasCoreEntityFullHeightSidebarFF;

    const headerEl = (
        <Header
            lead={lead}
            onUpdateLeadName={handleUpdateName}
            refetchLead={refetchLead}
            onUpdateTags={handleUpdateTags}
            fetchTags={fetchMoreTags}
            onUpdateLeadConfidence={handleUpdateConfidence}
            onUpdateLeadDueDate={handleUpdateDueDate}
            onUpdateLeadCreatedDate={handleUpdateCreatedDate}
            onUpdateLeadClosedDate={handleUpdateClosedDate}
        />
    );

    const contentEl = (
        <ContentM3
            lead={lead}
            scrollContainerRef={scrollContainerEl}
            user={props.user}
            hasScrolledPastCheckpoint={hasScrolledPastCheckpoint}
            getActionBarRef={setActionBarRef}
            isSidebarCollapsed={sidebarCollapsedState}
            scrollContainerTop={scrollContainerTop}
            isLeadRecapFeatureEnabled={isLeadRecapFeatureEnabled}
            hasFullHeightSidebar={hasFullHeightSidebar}
        />
    );

    const sidebarEl = (
        <Sidebar
            lead={lead}
            user={props.user}
            hasScrolledPastCheckpoint={hasScrolledPastCheckpoint}
            hasFullHeightSidebar={hasFullHeightSidebar}
        />
    );

    return (
        <CoreEntityPageContainer
            title={`Lead · ${lead.name}`}
            hasFullHeightSidebar={hasFullHeightSidebar}
            scrollContainerRef={hasFullHeightSidebar ? undefined : setScrollContainerRef}
            id={hasFullHeightSidebar ? undefined : 'lead-processes-container'}
            attributes={[
                // NUT-29554 - Old code searches for this id and extracts data attributes
                {key: 'data-entity-id', value: getIdFromApiId(lead.id)},
                {key: 'data-api-id', value: lead.id},
            ]}
        >
            {hasFullHeightSidebar ? (
                <>
                    <CoreEntityMainColumn
                        scrollContainerRef={setScrollContainerRef}
                        id='lead-processes-container'
                        // NUT-29554 - same element as scroll container when full-height sidebar is on
                        data-entity-id={getIdFromApiId(lead.id)}
                        data-api-id={lead.id}
                    >
                        {headerEl}
                        {contentEl}
                    </CoreEntityMainColumn>
                    {sidebarEl}
                </>
            ) : (
                <>
                    {headerEl}
                    <CoreEntityContentContainer>
                        {contentEl}
                        {sidebarEl}
                    </CoreEntityContentContainer>
                </>
            )}
        </CoreEntityPageContainer>
    );
}
